import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  MenuItem,
  Alert,
  InputAdornment,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import AppDialog, { AppDialogTitle, AppDialogContent, AppDialogActions } from '../layout/AppDialog';
import { createCustomer } from '../../services/api';
import { formatKenyanPhone, isValidKenyanPhone } from '../../utils/phoneUtils';

const AddCustomerModal = ({ open, onClose, onCustomerCreated, companies, isSuperAdmin, isCusAdmin, currentUserCompanyId }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '+254',
    address: '',
    type: 1,
    company_id: ''
  });

  const [phoneError, setPhoneError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formValid, setFormValid] = useState(false);

  // Validate the form whenever formData changes
  useEffect(() => {
    const validateForm = () => {
      // Required fields check
      const requiredFieldsValid = formData.name && formData.phone && (!isSuperAdmin || formData.company_id);
      
      // Phone validation
      const phoneValid = formData.phone && isValidKenyanPhone(formData.phone);
      setPhoneError(phoneValid ? '' : 'Phone must be in format +254XXXXXXXXX');
      
      setFormValid(requiredFieldsValid && phoneValid);
    };
    
    validateForm();
  }, [formData, isSuperAdmin]);

  // Set default company for cus-admin users
  useEffect(() => {
    if (isCusAdmin && currentUserCompanyId && !formData.company_id) {
      setFormData(prev => ({
        ...prev,
        company_id: currentUserCompanyId
      }));
    }
  }, [isCusAdmin, currentUserCompanyId, formData.company_id]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        name: '',
        phone: '+254',
        address: '',
        type: 1,
        company_id: isSuperAdmin && companies.length > 0 ? companies[0].id : ''
      });
      setError('');
      setPhoneError('');
    }
  }, [open, isSuperAdmin, companies]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      // Format phone number when it changes
      const formattedPhone = formatKenyanPhone(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedPhone
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formValid) return;
    
    setLoading(true);
    setError('');

    try {
      const response = await createCustomer(formData);
      onCustomerCreated(response.data.customer);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create customer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppDialog open={open} onClose={onClose}>
      <AppDialogTitle onClose={onClose} subtitle="Register a new customer record">
        Add New Customer
      </AppDialogTitle>
      <form onSubmit={handleSubmit}>
        <AppDialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <TextField
            fullWidth
            required
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            margin="normal"
          />
          
          <TextField
            fullWidth
            required
            label="Phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            error={!!phoneError}
            helperText={phoneError || "Format: +254XXXXXXXXX"}
            margin="normal"
            InputProps={{
              startAdornment: formData.phone && !formData.phone.startsWith('+') ? (
                <InputAdornment position="start">+</InputAdornment>
              ) : null
            }}
          />
          
          <TextField
            fullWidth
            label="Address (optional)"
            name="address"
            value={formData.address}
            onChange={handleChange}
            margin="normal"
            multiline
            rows={3}
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel id="type-select-label">Type</InputLabel>
            <Select
              labelId="type-select-label"
              name="type"
              value={formData.type}
              onChange={handleChange}
              label="Type"
              required
            >
              <MenuItem value={1}>Individual</MenuItem>
              <MenuItem value={2}>Company</MenuItem>
            </Select>
          </FormControl>
          
          {isSuperAdmin && (
            <FormControl fullWidth margin="normal">
              <InputLabel id="company-select-label">Company</InputLabel>
              <Select
                labelId="company-select-label"
                name="company_id"
                value={formData.company_id}
                onChange={handleChange}
                label="Company"
                required
              >
                {companies.map(company => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </AppDialogContent>
        
        <AppDialogActions>
          <Button onClick={onClose} variant="outlined" color="inherit">Cancel</Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading || !formValid}
          >
            {loading ? 'Creating...' : 'Create Customer'}
          </Button>
        </AppDialogActions>
      </form>
    </AppDialog>
  );
};

export default AddCustomerModal;

